import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import {
  CreateQueryRecordMutation,
  CreateQueryRecordMutationVariables,
  Engagements,
  GenerateSyntheticResultMutation,
  GenerateSyntheticResultMutationVariables,
  GetQueryRecordQuery,
  GetQueryRecordQueryVariables,
  IdentifierType,
  MetadataColumns,
  OnExecutionUpdateQueryRecordsSubscription,
  OnExecutionUpdateQueryRecordsSubscriptionVariables,
  OnUpdateQueryRecordsSubscription,
  OnUpdateQueryRecordsSubscriptionVariables,
  OnValidateQueryRecordsSubscription,
  OnValidateQueryRecordsSubscriptionVariables,
  QueryRecord,
  QueryRecordInput,
  QueryType,
  Status,
  SubmitQueryInput,
  SubmitQueryRecordMutation,
  SubmitQueryRecordMutationVariables,
  SyntheticActions,
  SyntheticResultStatus,
  UpdateExecutionQueryRecordMutation,
  UpdateExecutionQueryRecordMutationVariables,
  UpdateQueryInput,
  UpdateQueryRecordMutation,
  ValidateQueryRecordQuery,
  ValidateQueryRecordQueryVariables,
  GenerateAiQueryMutation,
  GenerateAiQueryMutationVariables,
  GenerateAiQueryInput,
  OnNotifyJobCompleteSubscription,
  OnNotifyJobCompleteSubscriptionVariables,
} from '../../../../API';
import {
  CreateQueryRecordForm,
  DataBaseTableItem,
  TypeDropdown,
  ValidationResponse,
} from '../../models/query-interface';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ApiService } from '../../../shared/services/api.service';
import {
  CreateQueryRecord,
  GenerateAiQuery,
  GenerateSyntheticResult,
  SubmitQueryRecord,
  UpdateQueryRecord,
} from '../../../../graphql/mutations';
import { GetQueryRecord, ValidateQueryRecord } from '../../../../graphql/queries';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { AppService } from '../../../shared/services/app.service';
import { editorOptions } from '../../models/editor-config/editor-config';
import { ActivatedRoute, Router } from '@angular/router';
import { AT_LEAST_ONE_ALPHABET, NOT_ALLOWED_SPECIAL_CHARS } from '../../../shared/utils/constants';
import { distinctUntilChanged, Subject, Subscription, takeUntil } from 'rxjs';
import {
  onExecutionUpdateQueryRecords,
  onNotifyJobComplete,
  onUpdateQueryRecords,
  onValidateQueryRecords,
} from '../../../../graphql/subscriptions';
import { MonacoEditorComponent } from '../../../shared/components/monaco-editor/monaco-editor.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ViewDetailsModalComponent } from '../view-details-modal/view-details-modal.component';
import { AiChatService, ChatMessage } from '../../services/ai-chat.service';

@Component({
  selector: 'app-query-builder',
  templateUrl: './query-builder.component.html',
  styleUrl: './query-builder.component.scss',
})
export class QueryBuilderComponent implements OnInit, OnDestroy {
  @Input() queryId!: string | null;
  @Output() navigateToHistory = new EventEmitter<void>();
  @ViewChild('queryEditor') queryEditor!: MonacoEditorComponent;

  /** selected engagement */
  engagements: Engagements | null = null;
  dataSourceList: string[] = [];
  tableMap = new Map<string, DataBaseTableItem[]>();
  dataSourceToDatabasesMap = new Map<string, string[]>();
  tableList: DataBaseTableItem[] = [];
  databaseList: Set<string> = new Set<string>();

  selectedDatasources: UntypedFormControl = new FormControl([], [Validators.required]);
  previousDatasourcesValue: string[] | null = null;
  selectedDatabases: UntypedFormControl = new FormControl([], [Validators.required]);

  queryRecordFormGroup: FormGroup<CreateQueryRecordForm>;
  /** default query types */
  readonly queryTypes: TypeDropdown[] = [
    {
      name: 'List',
      value: 'LIST',
    },
    {
      name: 'Aggregate',
      value: 'AGGREGATE',
    },
  ];
  validationLogs?: ValidationResponse;
  validationException?: string;
  validationStatus: Status | null | undefined;
  checkValidationStatus = false;
  isLoading = false;
  spinningState: 'validate' | 'save' | 'submit' | 'update' | 'generateSynthetic' | null = null;
  isQueryExpanded = false;
  isSyntheticResultSuccess: boolean = false;
  isSyntheticGenerationInProgress: boolean = false;
  queryRecord?: QueryRecord;

  readonly editorOptions: any = editorOptions;
  private destroy$ = new Subject<void>();
  graphqlValidationSubscription?: Subscription;
  isValidationSuccess = false;
  isValidating = false;
  graphqlUpdateSubscription?: Subscription;
  graphqlSubscription?: Subscription;

  code = '';
  placeholder = '';
  avatarLabel = 'User';
  messages: ChatMessage[] = [];
  isOpen = false;
  prompt = '';
  samples = [
    'Create a query that groups users by state, counts unique users in each state, and excludes internal users.',

    'Build a query that returns unique customers per country while removing inactive accounts.',

    'Count unique buyers per month and exclude test transactions.',
  ];
  copiedSql = '';
  jobId: string = '';
  isAiGeneratedQueryLoad: boolean = false;
  graphqlNotifyJobSubscription?: Subscription;

  constructor(
    private appService: AppService,
    private fb: FormBuilder,
    private nzMessageService: NzMessageService,
    private userManagementService: UserManagementService,
    private apiService: ApiService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private modalService: NzModalService,
    private aiChatService: AiChatService,
  ) {
    this.queryRecordFormGroup = this.fb.nonNullable.group({
      engagement: ['', Validators.required],
      orgType: ['', Validators.required],
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
          Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
        ],
      ],
      description: ['', [Validators.required, Validators.maxLength(500), Validators.pattern(AT_LEAST_ONE_ALPHABET)]],
      query: [
        '--Write your SQL query here',
        [Validators.required, Validators.maxLength(40000), Validators.pattern(AT_LEAST_ONE_ALPHABET)],
      ],
      type: ['', Validators.required],
      createdBy: ['', Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.queryId = null;
    this.engagements = null;
    this.graphqlValidationSubscription?.unsubscribe();
    this.graphqlSubscription?.unsubscribe();
    this.graphqlUpdateSubscription?.unsubscribe();
    this.graphqlNotifyJobSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    /** subscribe for engagement changes */
    this.appService.activeEngagement$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev.engagement !== curr.engagement),
      )
      .subscribe((engagements: Engagements) => {
        this.engagements = engagements;
        const dataSourceList: string[] = [];
        engagements?.metadata?.forEach((item) => {
          const { datasource_name = '', database_name = '', columns, table_name = '' } = item;
          dataSourceList.push(datasource_name as string);
          this.addTableToMap(datasource_name as string, database_name as string, {
            table_name,
            columns,
          });
          this.addDatabaseToMap(datasource_name as string, database_name as string);
        });
        this.dataSourceList = Array.from(new Set(dataSourceList));
        this.listenRealTimeValidationChanges(this.engagements.engagement);
        this.listenRealtimeUpdateMutationChanges(this.engagements.engagement);
        this.listenRealtimeChanges(this.engagements.engagement);

        if (this.queryId && this.engagements.engagement) {
          this.apiService
            .query<GetQueryRecordQuery, GetQueryRecordQueryVariables>(GetQueryRecord, {
              engagementId: this.engagements.engagement,
              queryId: this.queryId,
            })
            .then((result) => {
              const queryRecord = result.data?.GetQueryRecord;
              this.queryRecord = queryRecord as QueryRecord;
              const {
                name = '',
                description = '',
                type = '',
                query = '',
                validationLogs = undefined,
                validationStatus,
              } = queryRecord || {};
              if (queryRecord) {
                this.queryRecordFormGroup.patchValue(
                  {
                    engagement: this.engagements?.engagement,
                    orgType: this.userManagementService.getOrgType(),
                    createdBy: this.userManagementService.getEmail(),
                    name: name || '',
                    description: description || '',
                    type: type || '',
                    query: query || '',
                  },
                  { emitEvent: false },
                );
                this.validationLogs = !validationLogs || JSON.parse(validationLogs);
                this.validationStatus = validationStatus;
                this.isSyntheticResultSuccess = queryRecord.syntheticResult === SyntheticResultStatus.SUCCESS;
                if (this.validationStatus === Status.IN_PROGRESS) {
                  this.checkValidationStatus = true;
                  this.isValidationSuccess = false;
                  this.isValidating = true;
                } else if (this.validationStatus === Status.SUCCESS) {
                  this.checkValidationStatus = true;
                  this.isValidationSuccess = true;
                } else if (this.validationStatus === Status.FAIL) {
                  this.checkValidationStatus = false;
                  this.isValidationSuccess = false;
                }

                this.activatedRoute.queryParams.subscribe((params) => {
                  const database = params['database'];
                  const datasource = params['datasource'];

                  datasource && this.selectedDatasources.setValue(datasource);
                  database && this.selectedDatabases.setValue(database);
                });

                setTimeout(() => this.subscribeToQueryRecordFormChangeEvent(), 0);
              } else {
                this.nzMessageService.error('Failed to fetch query record');
              }
            })
            .catch((error) => {
              this.nzMessageService.error(error?.message ?? 'Failed to fetch query record');
            });
        } else {
          this.subscribeToQueryRecordFormChangeEvent();
          this.queryRecordFormGroup.patchValue({
            engagement: this.engagements?.engagement,
            orgType: this.userManagementService.getOrgType(),
            createdBy: this.userManagementService.getEmail(),
          });
        }
      });

    this.selectedDatasources.valueChanges.subscribe((datasources) => {
      this.previousDatasourcesValue
        ?.filter((prev) => datasources.indexOf(prev) === -1)
        .flatMap((removedDataSource) => this.dataSourceToDatabasesMap.get(removedDataSource))
        .forEach((removedDatabase) => {
          const currentDatabaseSelection = this.selectedDatabases.value.filter(
            (currentDb: string) => currentDb !== removedDatabase,
          );
          this.selectedDatabases.setValue(currentDatabaseSelection);
        });

      this.databaseList = new Set<string>(
        datasources.map((dataSource: string) => this.dataSourceToDatabasesMap.get(dataSource) || []).flat(),
      );
      this.previousDatasourcesValue = datasources;
    });

    this.selectedDatabases.valueChanges.subscribe(
      (databases: string[]) =>
        (this.tableList = this.selectedDatasources.value
          .map((dataSource: string[]) =>
            databases.map((database) => this.tableMap.get(`${dataSource}#${database}`) || []).flat(),
          )
          .flat()),
    );
    this.avatarLabel = this.getUserInitials(this.userManagementService.getEmail());
    this.messages = this.aiChatService.messages;
  }

  private subscribeToQueryRecordFormChangeEvent() {
    this.queryRecordFormGroup.valueChanges.subscribe(() => {
      if (this.validationStatus) {
        this.checkValidationStatus = false;
        this.isValidationSuccess = false;
        this.validationLogs = undefined;
        this.validationException = undefined;
      }
    });
  }

  addTableToMap(
    dataSource: string,
    databaseName: string,
    item: { table_name: string | null; columns?: MetadataColumns[] | null },
  ) {
    const key = `${dataSource}#${databaseName}`;
    const tableItem: DataBaseTableItem = {
      datasourceName: dataSource,
      databaseName: databaseName,
      tableName: item.table_name || '',
      columns: item.columns || [],
    };
    let tableList = this.tableMap.get(key);
    if (tableList) {
      tableList = [...tableList, tableItem];
    } else {
      tableList = [tableItem];
    }
    this.tableMap.set(key, tableList);
  }

  addDatabaseToMap(dataSource: string, databaseName: string) {
    let databaseList = this.dataSourceToDatabasesMap.get(dataSource);
    if (databaseList) {
      databaseList = [...databaseList, databaseName];
    } else {
      databaseList = [databaseName];
    }
    this.dataSourceToDatabasesMap.set(dataSource, databaseList);
  }

  saveQueryRecordApi(): Promise<void> {
    const { value } = this.queryRecordFormGroup;
    const queryRecordInput: QueryRecordInput = {
      ...value,
      engagement: value.engagement!,
      lastModifiedBy: this.userManagementService.getEmail(),
    };
    if (this.validationStatus) {
      queryRecordInput.validationStatus = this.validationStatus;
    }
    return this.apiService
      .query<CreateQueryRecordMutation, CreateQueryRecordMutationVariables>(CreateQueryRecord, {
        queryRecordInput,
      })
      .then((result) => {
        this.queryId = result.data?.CreateQueryRecord.queryId as string;

        if (result.errors?.length) {
          this.nzMessageService.error('Failed to save query');
          throw new Error('Failed to save query');
        }
        this.nzMessageService.success('Query Saved Successfully');
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message ?? 'Failed to save query');
      });
  }

  updateQueryRecordApi(): Promise<void> {
    const { name, description, query, type, engagement = '' } = this.queryRecordFormGroup.value;
    const updateQueryPayload: UpdateQueryInput = {
      queryId: this.queryId!,
      name,
      description,
      query,
      type: type as QueryType,
      engagement,
      orgType: this.userManagementService.getOrgType(),
      lastModifiedBy: this.userManagementService.getEmail(),
    };
    if (this.validationStatus) {
      updateQueryPayload.validationStatus = this.validationStatus;
    }
    return this.apiService
      .query<UpdateQueryRecordMutation, UpdateExecutionQueryRecordMutationVariables>(UpdateQueryRecord, {
        updateQueryRequest: updateQueryPayload,
      })
      .then((result) => {
        const updateRecord = result.data?.UpdateQueryRecord;
        if (result.errors?.length) {
          this.nzMessageService.error('Failed to update query');
          throw new Error('Failed to update query');
        }
        if (updateRecord?.validationStatus === Status.IN_PROGRESS) {
          this.checkValidationStatus = true;
          this.spinningState = null;
        }
        this.nzMessageService.success('Query Updated Successfully');
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message ?? 'Failed to update query');
      });
  }

  async validateQueryRecordApi(): Promise<void> {
    this.validationException = '';
    this.validationLogs = undefined;

    try {
      await this.apiService.query<ValidateQueryRecordQuery, ValidateQueryRecordQueryVariables>(ValidateQueryRecord, {
        queryRecord: {
          engagement: this.engagements?.engagement,
          query: this.queryFormControl.value,
          queryType: this.queryTypeFormControl.value,
          queryId: this.queryId,
          lastModifiedBy: this.userManagementService.getEmail(),
          validationStatus: Status.IN_PROGRESS,
        },
      });
      this.nzMessageService.info(
        'Validation has started. This may take some time. You may continue working or navigate elsewhere.',
        { nzDuration: 8000 },
      );
    } catch (error: any) {
      this.nzMessageService.error(`Unable to start validation: ${error}`);
    }
  }

  updateValidationStatusApi(validationStatus: Status): Promise<void> {
    this.isLoading = true;
    this.spinningState = 'submit';

    const updateQueryPayload: UpdateQueryInput = {
      engagement: this.engagementFormControl.value,
      queryId: this.queryId!,
      lastModifiedBy: this.userManagementService.getEmail(),
      validationLogs: JSON.stringify(this.validationLogs || []),
      validationStatus,
    };

    return this.apiService
      .query<UpdateExecutionQueryRecordMutation, UpdateExecutionQueryRecordMutationVariables>(UpdateQueryRecord, {
        updateQueryRequest: updateQueryPayload,
      })
      .then(); // Mock then to avoid unnecessary type inferring
  }

  handleSave(): void {
    this.isLoading = true;
    this.spinningState = 'save';
    this.validationStatus = null;
    this.saveQueryRecordApi()
      .then(() =>
        this.router.navigate(['/query/edit', this.queryId], {
          queryParams: {
            database: this.selectedDatabases.value,
            datasource: this.selectedDatasources.value,
          },
        }),
      )
      .finally(() => this.resetLoading());
  }

  handleUpdate(): void {
    this.isLoading = true;
    this.spinningState = 'update';
    this.validationStatus = null;
    this.updateQueryRecordApi().finally(() => this.resetLoading());
  }

  handleValidate(): void {
    this.isLoading = true;
    this.spinningState = 'validate';
    this.isValidating = true;

    this.validationStatus = Status.IN_PROGRESS;
    if (!this.queryId) {
      this.saveQueryRecordApi()
        .then(() => this.validateQueryRecordApi())
        .then(() =>
          this.router.navigate(['/query/edit', this.queryId], {
            queryParams: {
              database: this.selectedDatabases.value,
              datasource: this.selectedDatasources.value,
            },
          }),
        );
      return;
    }
    this.updateQueryRecordApi().then(() => this.validateQueryRecordApi());
  }

  handleGenerateSyntheticResult(): void {
    this.isLoading = true;
    this.spinningState = 'generateSynthetic';
    this.isSyntheticGenerationInProgress = true;

    if (this.queryRecord) {
      this.queryRecord = {
        ...this.queryRecord,
        syntheticResult: SyntheticResultStatus.IN_PROGRESS,
        athenaExecutionId: null,
      };
    }

    this.apiService
      .query<GenerateSyntheticResultMutation, GenerateSyntheticResultMutationVariables>(GenerateSyntheticResult, {
        syntheticDataInput: {
          action: SyntheticActions.GENERATE_RESULT,
          engagement: this.engagementFormControl.value,
          queryId: this.queryId!,
        },
      })
      .then((result) => {
        if (result.errors?.length) {
          this.nzMessageService.error('Failed to generate synthetic result');
          return;
        }
        this.nzMessageService.success('Synthetic result generation has started. This may take some time.');
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message ?? 'Failed to generate synthetic result');
      })
      .finally(() => {
        this.resetLoading();
      });
  }

  submitQueryRecord(): void {
    this.isLoading = true;
    this.spinningState = 'submit';

    const submitQueryPayload: SubmitQueryInput = {
      engagement: this.engagementFormControl.value,
      queryId: this.queryId!,
      lastModifiedBy: this.userManagementService.getEmail(),
    };

    this.apiService
      .query<SubmitQueryRecordMutation, SubmitQueryRecordMutationVariables>(SubmitQueryRecord, {
        updateQueryRequest: submitQueryPayload,
      })
      .then((result) => {
        if (result.errors?.length) {
          this.nzMessageService.error('Failed to Submit query');
          return;
        }
        this.nzMessageService.success('Query Submitted Successfully');
        this.redirectToHistory();
      })
      .catch((error) => {
        this.nzMessageService.error(error?.message ?? 'Failed to submit query');
      })
      .finally(() => {
        this.resetLoading();
      });
  }

  resetLoading(): void {
    this.isLoading = false;
    this.spinningState = null;
  }

  redirectToHistory() {
    this.router.navigate(['/query'], {
      queryParams: { tabIndex: 1 },
    });
  }

  toggleQueryExpanded(): void {
    this.isQueryExpanded = !this.isQueryExpanded;
  }

  onKeyDown(event: KeyboardEvent): void {
    // Escape key to collapse when expanded
    if (event.key === 'Escape' && this.isQueryExpanded) {
      this.toggleQueryExpanded();
      event.preventDefault();
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  get nameFormControl(): UntypedFormControl {
    return this.queryRecordFormGroup.get('name') as UntypedFormControl;
  }

  get queryFormControl(): UntypedFormControl {
    return this.queryRecordFormGroup.get('query') as UntypedFormControl;
  }

  get descriptionFormControl(): UntypedFormControl {
    return this.queryRecordFormGroup.get('description') as UntypedFormControl;
  }

  get queryTypeFormControl(): UntypedFormControl {
    return this.queryRecordFormGroup.get('type') as UntypedFormControl;
  }

  get engagementFormControl(): UntypedFormControl {
    return this.queryRecordFormGroup.get('engagement') as UntypedFormControl;
  }

  set orgTypeFormControl(orgType: string) {
    this.queryRecordFormGroup.get('orgType')?.setValue(orgType);
  }

  set createdByFormControl(createdBy: string) {
    this.queryRecordFormGroup.get('createdBy')?.setValue(createdBy);
  }

  showIdentifierTag(identifier: string | null): boolean {
    return identifier === IdentifierType.EMAIL || identifier === IdentifierType.UNIQUE;
  }

  listenRealTimeValidationChanges(engagement: string): void {
    this.graphqlValidationSubscription = this.apiService
      .subscription<OnValidateQueryRecordsSubscription, OnValidateQueryRecordsSubscriptionVariables>(
        onValidateQueryRecords,
        { engagement },
      )
      .subscribe({
        next: (value) => {
          const updatedItem = value?.data?.onValidateQueryRecords;
          if (updatedItem && updatedItem.validationStatus && updatedItem.validationStatus !== Status.IN_PROGRESS) {
            this.updateStatusItem(updatedItem);
          }
        },
        error: (error: any) => {
          console.warn('Subscription error: ', error);
        },
      }) as Subscription;
  }

  updateStatusItem(updatedItem: QueryRecord): void {
    if (this.queryId === updatedItem.queryId) {
      if (!this.isValidating) return;
      this.queryRecord = {
        ...updatedItem,
      };
      this.isSyntheticResultSuccess = false;
      this.validationLogs = !updatedItem?.validationLogs || JSON.parse(updatedItem.validationLogs);
      this.announceValidationStatusUpdate(updatedItem);
      if (updatedItem?.validationStatus === Status.SUCCESS) {
        this.checkValidationStatus = true;
        this.isValidationSuccess = true;
      } else if (updatedItem.validationStatus === Status.FAIL) {
        this.checkValidationStatus = false;
        this.isValidationSuccess = false;
      }
      this.resetLoading();
      this.isValidating = false;
    }
  }

  announceValidationStatusUpdate(newItem: QueryRecord): void {
    const { name: queryName, validationStatus } = newItem;
    let msg = '';
    switch (validationStatus) {
      case Status.SUCCESS: {
        msg = `Validation of Query ${queryName} is completed successfully.`;
        break;
      }

      case Status.FAIL: {
        msg = `Validation of Query ${queryName} is failed.`;
        break;
      }
    }
    this.nzMessageService.info(msg, {
      nzDuration: 5000,
    });
  }

  onAthenaErrorClick(err: any) {
    if (!err.includes('[ATHENA_ERROR]')) {
      return;
    }

    const match = err.match(/line\s+(\d+):(\d+)/i);

    if (!match) return;

    const athenLine = parseInt(match[1], 10);
    const column = match[2] ? parseInt(match[2], 10) : 1;

    const adjustedLine = this.queryEditor.translateAthenaLine(athenLine);

    this.queryEditor.navigateToError(adjustedLine, column);
    this.queryEditor.highlightErrorLine(adjustedLine);
  }

  viewQueryRecord() {
    this.modalService.create({
      nzTitle: 'View Query Details',
      nzContent: ViewDetailsModalComponent,
      nzData: this.queryRecord,
      nzOkText: null,
      nzCancelText: null,
      nzWidth: '70rem',
      nzFooter: null,
    });
  }

  isClickableError(error: string | null): boolean {
    if (!error) return false;
    return error.includes('[ATHENA_ERROR]') && /line\s+(\d+):(\d+)/i.test(error);
  }

  listenRealtimeUpdateMutationChanges(engagement: string): void {
    this.graphqlUpdateSubscription = this.apiService
      .subscription<OnUpdateQueryRecordsSubscription, OnUpdateQueryRecordsSubscriptionVariables>(onUpdateQueryRecords, {
        engagement,
      })
      .subscribe({
        next: (value) => {
          const updatedItem = value?.data?.onUpdateQueryRecords;
          if (updatedItem) {
            this.updateItemInList(updatedItem, this.queryRecord);
          }
        },
        error: (error: any) => console.warn('Subscription error:', error),
      }) as Subscription;
  }

  updateItemInList(updatedItem: QueryRecord, queryRecord: QueryRecord | undefined): void {
    if (queryRecord?.queryId === updatedItem.queryId) {
      const syntheticJustCompleted =
        updatedItem.syntheticResult === SyntheticResultStatus.SUCCESS &&
        queryRecord.syntheticResult !== SyntheticResultStatus.SUCCESS;

      if (syntheticJustCompleted) {
        this.isSyntheticResultSuccess = true;
        if (this.queryRecord) {
          this.queryRecord.athenaExecutionId = updatedItem.athenaExecutionId;
          this.queryRecord.syntheticResult = updatedItem.syntheticResult;
        }
        this.isSyntheticGenerationInProgress = false;
        this.nzMessageService.success('Synthetic Results are ready');
      } else if (
        updatedItem.syntheticResult === SyntheticResultStatus.FAIL &&
        queryRecord?.syntheticResult !== SyntheticResultStatus.FAIL
      ) {
        if (this.queryRecord) {
          this.queryRecord.syntheticResult = updatedItem.syntheticResult;
        }
        this.isSyntheticGenerationInProgress = false;
        this.nzMessageService.error('Synthetic Results generation failed');
      }
    }
  }

  listenRealtimeChanges(engagement: string): void {
    this.graphqlSubscription = this.apiService
      .subscription<OnExecutionUpdateQueryRecordsSubscription, OnExecutionUpdateQueryRecordsSubscriptionVariables>(
        onExecutionUpdateQueryRecords,
        {
          engagement,
        },
      )
      .subscribe({
        next: (value) => {
          const updatedItem = value?.data?.onExecutionUpdateQueryRecords;
          if (updatedItem) {
            this.updateItemInList(updatedItem, this.queryRecord);
          }
        },
        error: (error: any) => console.warn('Subscription error:', error),
      }) as Subscription;
  }

  getUserInitials(email: string): string {
    const username = email.split('@')[0];
    if (!username) {
      return '';
    }
    const [firstName, lastName] = username.split('.');
    return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase();
  }

  getPlaceHolder() {
    const msg =
      this.messages.length > 0
        ? 'Ask for changes'
        : 'Describe your ideas to initiate writing queries. You can also ask for changes once the query is written.';
    return msg;
  }

  sendMessage(): void {
    if (!this.prompt?.trim()) {
      return;
    }

    const userText = this.prompt;

    this.aiChatService.messages.push({
      type: 'user',
      text: userText,
    });
    this.messages = this.aiChatService.messages;

    this.prompt = '';

    // Mock AI Response
    this.isAiGeneratedQueryLoad = true;
    this.getGeneratedAiQuery(userText);

    setTimeout(() => {
      const body = document.querySelector('.chat-content');
      body?.scrollTo({
        top: body.scrollHeight,
        behavior: 'smooth',
      });
    });
  }

  closePanel() {
    this.isOpen = false;
  }

  selectSample(prompt: string): void {
    this.prompt = prompt;
    this.sendMessage();
  }
  clearChat(): void {
    this.aiChatService.messages = [];
    this.messages = [];
    this.jobId = '';
  }

  insertQueryInQueryInputArea(): void {
    if (!this.copiedSql) {
      this.nzMessageService.warning('Please copy a query first.');
      return;
    }

    this.queryEditor.insertTextAtCursor(this.copiedSql);

    this.nzMessageService.success('Query inserted at the current cursor position in the Query Input Area.');
  }

  onCodeCopied(sql: string): void {
    this.copiedSql = sql;
    this.insertQueryInQueryInputArea();
  }

  getGeneratedAiQuery(prompt: string): void {
    const generateAiQueryInput: GenerateAiQueryInput = {
      analystId: this.userManagementService.getEmail(),
      engagement: this.engagements?.engagement || '',
      queryType: (this.queryRecordFormGroup.value.type as QueryType) || QueryType.AGGREGATE,
      userQuery: prompt,
      jobId: this.jobId,
    };
    this.apiService
      .query<GenerateAiQueryMutation, GenerateAiQueryMutationVariables>(GenerateAiQuery, {
        generateAiQueryInput,
      })
      .then((response) => {
        const res = response?.data?.GenerateAiQuery;
        if (res?.statusCode == 200 && res?.body) {
          const body = JSON.parse(res?.body);
          if (this.jobId !== body.jobId) {
            this.jobId = body.jobId;
            if (this.graphqlNotifyJobSubscription) {
              this.graphqlNotifyJobSubscription?.unsubscribe();
            }
            this.subscribeToGeneratedAiQueryNotification(this.jobId);
          }
        }
      })
      .catch((error) => {
        console.warn('Ai query generated error:', error);
      });
  }

  subscribeToGeneratedAiQueryNotification(jobId: string): void {
    this.graphqlNotifyJobSubscription = this.apiService
      .subscription<OnNotifyJobCompleteSubscription, OnNotifyJobCompleteSubscriptionVariables>(onNotifyJobComplete, {
        jobId: jobId,
      })
      .subscribe({
        next: (value) => {
          const response = value?.data?.onNotifyJobComplete;
          if (response?.statusCode == 200 && response?.body) {
            const generatedSQL = response?.body?.generatedSql;
            const generatedText = response?.body?.aiComments;
            if (!response?.body?.generatedSql) {
              return;
            }
            this.aiChatService.messages.push({
              type: 'bot',
              text: generatedText || '',
              sql: generatedSQL || '',
            });
            this.messages = this.aiChatService.messages;
          }
          this.isAiGeneratedQueryLoad = false;
        },
        error: (error: unknown) => {
          this.isAiGeneratedQueryLoad = false;
          console.warn('Ai query generated subscription error:', error);
        },
      }) as Subscription;
  }
  getThinkingMessage(): string {
    return 'Working on it...';
  }
}
